import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clientService, {
  type GetClientsParams,
  type AssignProvidersToClientPayload,
  type BulkUpdateClientProvidersPayload,
  type BulkBcbaPayload,
  type CaseloadConflictPreview,
  type PendingCaseloadTransfer,
  type TransferCaseloadPreviewResult,
} from '../services/api/clientService';
import type { CreateClientDto, UpdateClientDto, ClientStatus } from '../types/clients';

export function useClientList(params?: GetClientsParams) {
  return useQuery({
    queryKey: ['clients', 'list', params],
    queryFn: () => clientService.getClients(params),
    staleTime: 30_000,
  });
}

export function useClientDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'detail', id],
    queryFn: () => clientService.getClient(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useClientClinicians(clientId: string | undefined) {
  return useQuery({
    queryKey: ['clients', 'clinicians', clientId],
    queryFn: () => clientService.getClientClinicians(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateClientDto) => clientService.createClient(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateClientDto }) =>
      clientService.updateClient(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', id] });
    },
  });
}

/** Change a single client's status (also how an off-roster client is reactivated). */
export function useUpdateClientStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ClientStatus }) =>
      clientService.updateClientStatus(id, status),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', id] });
    },
  });
}

/** Bulk-change status for many clients at once. */
export function useBulkUpdateClientStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientIds, status }: { clientIds: string[]; status: ClientStatus }) =>
      clientService.bulkUpdateClientStatus(clientIds, status),
    onSuccess: (_data, { clientIds }) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Also invalidate each affected client's detail cache directly — mirrors
      // useUpdateClientStatus so a drawer already open on one of these clients
      // (staleTime: 30s) doesn't keep showing the pre-update status.
      clientIds.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: ['clients', 'detail', id] });
      });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

export function useAssignClinician(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => clientService.assignClinician(clientId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'clinicians', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
    },
  });
}

export function useRemoveClinician(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => clientService.removeClinician(clientId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'clinicians', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
    },
  });
}

/** Providers assignable to a client — lazy: only fetched when `enabled` (flyout open). */
export function useAssignableProviders(clientId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['clients', 'assignable-providers', clientId],
    queryFn: () => clientService.getAssignableProviders(clientId!),
    enabled: !!clientId && enabled,
    staleTime: 0,
  });
}

/** Assign one or many providers to a client (optionally set the caseload owner). */
export function useAssignProvidersToClient(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignProvidersToClientPayload) =>
      clientService.assignProvidersToClient(clientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'clinicians', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'assignable-providers', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'pending-transfer', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'list'] });
    },
  });
}

/** Org-wide providers for the bulk drawer — lazy: only fetched when `enabled` (drawer open). */
export function useAllAssignableProviders(enabled = true) {
  return useQuery({
    queryKey: ['clients', 'all-assignable-providers'],
    queryFn: () => clientService.listAllAssignableProviders(),
    enabled,
    staleTime: 60_000,
  });
}

/** Bulk assign/unassign many providers to/from many clients. */
export function useBulkUpdateClientProviders() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkUpdateClientProvidersPayload) =>
      clientService.bulkUpdateClientProviders(payload),
    onSuccess: () => {
      // Refresh the clients list (clinician counts) and any open client detail/clinicians.
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

/** Preview Overseeing-BCBA conflicts for selected clients — lazy (drawer open). */
export function useBulkBcbaPreview(clientIds: string[], enabled = true) {
  return useQuery({
    queryKey: ['clients', 'bulk-bcba-preview', clientIds],
    queryFn: () => clientService.previewBulkBcba(clientIds),
    enabled: enabled && clientIds.length > 0,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Bulk set or clear the Overseeing BCBA for many clients. */
export function useBulkUpdateBcba() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BulkBcbaPayload) => clientService.bulkUpdateBcba(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

/**
 * Conflict preview for a caseload-owner change on a single client.
 * Enabled only when `enabled` is true (i.e. when the conflict modal opens).
 * staleTime: 0 so the data is always fresh when the modal re-opens.
 */
export function useCaseloadConflict(
  clientId: string | undefined,
  newOwnerMemberId: string | undefined,
  options: { enabled: boolean },
) {
  return useQuery<CaseloadConflictPreview>({
    queryKey: ['clients', 'caseload-conflict', clientId, newOwnerMemberId],
    queryFn: () => clientService.getCaseloadConflict(clientId!, newOwnerMemberId!),
    enabled: !!clientId && !!newOwnerMemberId && options.enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Active pending caseload transfer for a client (drives the scheduled-transfer banner). */
export function usePendingTransfer(clientId: string | undefined) {
  return useQuery<PendingCaseloadTransfer | null>({
    queryKey: ['clients', 'pending-transfer', clientId],
    queryFn: () => clientService.getPendingTransfer(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

/**
 * Preview the aggregate open-Red-Dot impact of a bulk caseload transfer.
 * A mutation (not a query) since the wizard calls it on-demand — right before executing the
 * transfer — rather than reactively as its inputs change.
 */
export function useTransferCaseloadPreview() {
  return useMutation<TransferCaseloadPreviewResult, unknown, { fromMemberId: string; clientIds?: string[] }>({
    mutationFn: ({ fromMemberId, clientIds }) =>
      clientService.getTransferCaseloadPreview(fromMemberId, clientIds),
  });
}

/** Cancel an active pending caseload transfer. */
export function useCancelPendingTransfer(clientId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clientService.cancelPendingTransfer(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients', 'pending-transfer', clientId] });
      // Also refresh related client state so the panel reflects the cancelled transfer.
      queryClient.invalidateQueries({ queryKey: ['clients', 'assignable-providers', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'detail', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients', 'clinicians', clientId] });
    },
    onError: () => {
      // Refresh the pending-transfer query on failure too — in case it was already applied
      // (e.g. the cron ran between the user viewing it and clicking cancel).
      queryClient.invalidateQueries({ queryKey: ['clients', 'pending-transfer', clientId] });
    },
  });
}
